import React, { useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { FaFileCode } from 'react-icons/fa';
import ConfirmationPopup from '../Utils/ConfirmationPopup';
import QuestionnaireQuestionList from './QuestionnaireQuestionList';
import AddQuestionnaireQuestion from './Shards/Questions/AddQuestionnaireQuestion';
import CriteriaView from './Shards/CriteriaView';
import ImprovementsView from './Shards/ImprovementsView';
import RawJsonEditor from '../Utils/RawJsonEditor';
import { useSession } from '../Utils/SessionContext';
import { updateQuestionnaireQuestions } from './Utils/QuestionUpdater';
import toast from 'react-hot-toast';

/**
 * EvaluationQuestionnaire Component
 * Displays a questionnaire with questions and answers for evaluations
 * 
 * @param {Object} questionnaire - The questionnaire data to display
 * @param {boolean} showHeader - Whether to show the header section (default: true)
 */
const EvaluationQuestionnaire = ({ questionnaire, showHeader = true }) => {
  const { user } = useSession();
  const [confirmPopup, setConfirmPopup] = useState({ 
    show: false, 
    questionId: null,
    isFullQuestionnaire: false 
  });
  const [showCriteriaPopup, setShowCriteriaPopup] = useState(false);
  const [showImprovementsPopup, setShowImprovementsPopup] = useState(false);
  const [isRawEditOpen, setIsRawEditOpen] = useState(false);
  const [isAddQuestionOpen, setIsAddQuestionOpen] = useState(false);
  
  const handleRegenerateQuestion = (questionId) => {
    alert('Coming Soon! Question regeneration will be available shortly.');
  };
  
  const handleRegenerateFullQuestionnaire = () => {
    setConfirmPopup({ show: true, questionId: null, isFullQuestionnaire: true });
  };
  
  const confirmRegeneration = () => {
    if (confirmPopup.isFullQuestionnaire) {
      // Placeholder for regenerating the entire questionnaire
      console.log('Regenerating entire questionnaire');
    } else {
      // Placeholder for regenerating a specific question
      console.log(`Regenerating question with ID: ${confirmPopup.questionId}`);
    }
    setConfirmPopup({ show: false, questionId: null, isFullQuestionnaire: false });
  };
  
  const cancelRegeneration = () => {
    setConfirmPopup({ show: false, questionId: null, isFullQuestionnaire: false });
  };

  // Save function for questionnaire JSON using utility function
  const handleSaveQuestionnaire = async (editedData) => {
    if (!questionnaire?.questionnaire_id) {
      throw new Error('Questionnaire ID is required to save questionnaire');
    }

    try {
      console.log('Saving edited questionnaire JSON:', editedData);

      // Use utility function to update questions
      await updateQuestionnaireQuestions(
        questionnaire.questionnaire_id,
        editedData.questions,
        user
      );

      toast.success('Questionnaire updated successfully!');
      
      // Reload the page to refresh data
      window.location.reload();
    } catch (error) {
      console.error('Error updating questionnaire:', error);
      toast.error('Failed to update questionnaire. Please try again.');
      throw error; // Re-throw so RawJsonEditor can handle it
    }
  };
  
  if (!questionnaire?.questionnaire_json?.questions?.length) {
    return (
      <div className="space-y-4">
        {/* Header with Generate Button */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="mr-8">
              <h2 className="text-2xl font-bold text-gray-900">Questionnaire</h2>
              <p className="text-gray-600 mt-2">
                No questionnaire has been generated for this evaluation yet.
              </p>
            </div>
            
            {/* Generate Questionnaire Button */}
            <button
              onClick={() => toast('Functionality coming soon...', { icon: 'ℹ️' })}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center whitespace-nowrap"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Generate Questionnaire
            </button>
          </div>
        </div>
        
        {/* Empty State */}
        <div className="p-8 bg-gray-50 rounded-lg text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-600 text-lg">Click "Generate Questionnaire" to create a questionnaire for this evaluation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Confirmation Popup */}
      <ConfirmationPopup 
        show={confirmPopup.show}
        title="Confirm Regeneration"
        message={confirmPopup.isFullQuestionnaire 
          ? "Are you sure you want to regenerate and replace the entire questionnaire?" 
          : "Are you sure you want to regenerate and replace this question?"}
        onConfirm={confirmRegeneration}
        onCancel={cancelRegeneration}
      />
      
      {/* Optional Header */}
      {showHeader && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="mr-8">
              <h2 className="text-2xl font-bold text-gray-900">Questionnaire</h2>
              <p className="text-gray-600 mt-2">
                This displays the non-graded questionnaire questions.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white border border-gray-300 text-gray-700">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Approx. Time:&nbsp;<span className="font-bold">{questionnaire.max_duration_minutes} min</span>
                </span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              {/* Add Question button */}
              <button
                onClick={() => setIsAddQuestionOpen(true)}
                className="h-10 px-4 text-white rounded hover:opacity-90 transition-colors flex items-center whitespace-nowrap"
                style={{ backgroundColor: 'var(--color-brand-green)' }}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Question</span>
              </button>

              {/* Actions Dropdown */}
              <div className="relative group">
                <button className="h-10 px-4 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition-colors flex items-center whitespace-nowrap">
                  <span>Actions</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="py-1">
                    {/* View Criteria */}
                    <button
                      onClick={() => setShowCriteriaPopup(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 616 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      View Criteria
                    </button>
                    
                    {/* Raw JSON Edit - only visible for Superadmin */}
                    {user?.role === 'Superadmin' && (
                      <>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => setIsRawEditOpen(true)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                        >
                          <FaFileCode className="w-4 h-4 mr-2" />
                          Raw JSON Edit
                        </button>
                        
                        {/* Regenerate Questionnaire */}
                        <button
                          onClick={handleRegenerateFullQuestionnaire}
                          disabled
                          className="w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Regenerate Questionnaire
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Question Modal */}
      <AddQuestionnaireQuestion
        isOpen={isAddQuestionOpen}
        onClose={() => setIsAddQuestionOpen(false)}
        questionnaireId={questionnaire.questionnaire_id}
        allQuestions={questionnaire.questionnaire_json.questions}
        onSuccess={() => window.location.reload()}
      />

      {/* Questions */}
      <QuestionnaireQuestionList 
        questions={questionnaire.questionnaire_json.questions} 
        onRegenerateQuestion={handleRegenerateQuestion}
        showAnswers={false}
        questionnaireId={questionnaire.questionnaire_id}
        onQuestionUpdated={() => window.location.reload()}
      />
      
      {/* Criteria View Popup */}
      <CriteriaView
        show={showCriteriaPopup}
        onClose={() => setShowCriteriaPopup(false)}
        criteria_json={questionnaire?.questionnaire_criteria_json}
      />
      
      {/* Improvements View Popup */}
      {/* <ImprovementsView
        show={showImprovementsPopup}
        onClose={() => setShowImprovementsPopup(false)}
        questions={questionnaire?.questionnaire_json?.questions || []}
      /> */}
      
      {/* Raw Edit Questionnaire Modal */}
      <RawJsonEditor
        isOpen={isRawEditOpen}
        onClose={() => setIsRawEditOpen(false)}
        data={questionnaire?.questionnaire_json}
        title="Raw Edit Questionnaire"
        description="Edit the questionnaire JSON directly. This is a temporary solution which will be replaced with a form."
        rootName="questionnaire"
        hiddenAttributes={["llminstruction", "type", "placeholder", "userInput", "userInputOther", "time"]}
        onSave={handleSaveQuestionnaire}
        saveButtonText="Save"
      />
    </div>
  );
};

export default EvaluationQuestionnaire;
